import {
  EContractStatus,
  ECreditApplicationStep,
  EDecision,
  EDocumentStatus,
  EDocumentType,
  EFinancingCompany,
  EFinancingProgram,
  EWorksheetStatus,
} from '@if/core/types'
import {
  DefaultProgramConfig,
  FinancingCompanyConfigs,
  FinancingProgramConfigs,
  HiddenMerchantCategories,
} from '@src/types/Constants'
import type { CreditApplication } from '@src/types/CreditApplicationSchema'
import { Merchant } from '@src/types/Merchant'
import { RequiredDocument } from '@src/types/RequiredDocument'

export const programConfigByFinancingProgramId = (financingProgram: EFinancingProgram) => {
  return FinancingProgramConfigs[financingProgram] ?? DefaultProgramConfig
}

export const companyConfigByFinancingProgramId = (financingProgram: EFinancingProgram) => {
  const icebergConfig = FinancingCompanyConfigs[EFinancingCompany.Iceberg]
  const ifinanceConfig = FinancingCompanyConfigs[EFinancingCompany.IFinance]

  if (icebergConfig.supportedPrograms.includes(financingProgram)) {
    return icebergConfig
  }
  return ifinanceConfig
}

export const anyDocsAwaitingApproval = (requiredDocuments: RequiredDocument[]) => {
  return requiredDocuments?.some((doc) => doc.status === EDocumentStatus.AwaitingApproval)
}

export const anyDocsAwaitingUploadOrRefused = (
  requiredDocuments: RequiredDocument[],
  step = ECreditApplicationStep.Credit,
) => {
  const orderedSteps = [
    ECreditApplicationStep.Credit,
    ECreditApplicationStep.Financing,
    ECreditApplicationStep.CVT,
    ECreditApplicationStep.PayOut,
  ]
  const stepIndex = orderedSteps.indexOf(step)

  return requiredDocuments?.some(
    (doc) =>
      (doc.status === EDocumentStatus.AwaitingDocument ||
        doc.status === EDocumentStatus.Incomplete ||
        doc.status === EDocumentStatus.Refused ||
        doc.status === EDocumentStatus.Deleted) &&
      orderedSteps.indexOf(doc.requiredBeforeStep) <= stepIndex,
  )
}

export const getCanEdit = (isEditLocked: boolean) => {
  return !isEditLocked
}

export const getTimeStamp = () => {
  const now = new Date()

  const year = now.getUTCFullYear().toString()
  const month = (now.getUTCMonth() + 1).toString()
  const day = now.getUTCDate().toString()
  const hour = now.getUTCHours().toString()
  const minute = now.getUTCMinutes().toString()
  const second = now.getUTCSeconds().toString()

  return `${year}${month}${day}${hour}${minute}${second}`
}

export const cvtAwaitingApproval = (requiredDocuments: RequiredDocument[]) => {
  return requiredDocuments?.some(
    (doc) => doc.typeId === EDocumentType.SignedCVT && doc.status === EDocumentStatus.AwaitingApproval,
  )
}

export const areAllRequiredDocumentForStepApproved = (
  creditApp: CreditApplication,
  step: ECreditApplicationStep,
): boolean => {
  const requiredDocsForStep = creditApp.requiredDocuments.filter((doc) => doc.requiredBeforeStep === step)

  if (requiredDocsForStep.length === 0) {
    return true
  }

  return !requiredDocsForStep.some((doc) => doc.status !== EDocumentStatus.Approved)
}

export const checkCanCreateContract = (creditApp: CreditApplication) => {
  const documentsApprovedForCVT =
    areAllRequiredDocumentForStepApproved(creditApp, ECreditApplicationStep.CVT) &&
    areAllRequiredDocumentForStepApproved(creditApp, ECreditApplicationStep.Financing)

  return (
    documentsApprovedForCVT &&
    !creditApp.hasTasksBeforeCvt &&
    (creditApp.worksheet?.status === EWorksheetStatus.Active || creditApp.worksheet === null) &&
    creditApp.worksheet?.finalDecision.decision !== EDecision.Approved
  )
}

// TODO: Remove shouldDisplayInterest when we remove the UseMerchDBshowRate feature flag
export const shouldDisplayInterest = (creditApp: CreditApplication, isShowRateFlagActive: boolean) => {
  return !isShowRateFlagActive && !HiddenMerchantCategories.includes(creditApp.loanPurposeId) && creditApp.finalCreditDecision.interestRate > 0
}

export const mustShowInterestRate = (
  creditApp: CreditApplication,
  merchant: Merchant,
  isShowRateFlagActive: boolean,
) => {
  if (isShowRateFlagActive) {
    return merchant.showInterestRateOnMerchantDashboard
  }
  return shouldDisplayInterest(creditApp, isShowRateFlagActive)
}

export const availableCreditEqualApprovedAmount = (creditApp: CreditApplication) => {
  return creditApp.availableCreditAmount === creditApp.finalCreditDecision.maxAmountFinanced
}

export const getContractExpirationDate = (creditApp: CreditApplication): Date | null => {
  return creditApp.contract?.expiresOn ? new Date(creditApp.contract.expiresOn) : null
}

export const isContractExpired = (
  creditApp: CreditApplication,
  isFeatureActive: boolean,
  today: Date = new Date(),
): boolean => {
  if (!isFeatureActive) return false
  if (!creditApp.contract) return false
  if (!programConfigByFinancingProgramId(creditApp.financingProgramId).supportsContractUpdateOnExpiry) return false
  if (creditApp.contract.status === EContractStatus.Completed) return false
  const expiresAt = getContractExpirationDate(creditApp)
  return expiresAt != null && expiresAt < today
}

export function getAgeInYearsAndMonths(birthDate: string | Date, now: Date = new Date()) {
  const birth = new Date(birthDate)
  let ageYears = now.getFullYear() - birth.getFullYear()
  let ageMonths = (now.getFullYear() - birth.getFullYear()) * 12 + (now.getMonth() - birth.getMonth())
  if (now.getMonth() < birth.getMonth() || (now.getMonth() === birth.getMonth() && now.getDate() < birth.getDate())) {
    ageYears--
  }
  if (now.getDate() < birth.getDate()) ageMonths--
  return { ageYears, ageMonths }
}
